/**
 * lesson-plans/$id/print.tsx — A4 print view for a single lesson plan.
 *
 * Loads the plan via lessonPlanQuery(id) and renders <PrintLayout>. The
 * page applies @page A4 CSS and hides navigation/sidebar so the print
 * preview shows only the plan content.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PrintLayout } from '../../../components/lessons/PrintLayout.js';
import {
  freshSessionQuery,
  lessonPlanQuery,
  publicCatalogQuery,
} from '../../../lib/api/queries.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';

export const Route = createFileRoute('/lesson-plans/$id/print')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: PrintLessonPlanPage,
});

function PrintLessonPlanPage() {
  const { id } = Route.useParams();
  const plan = useQuery(lessonPlanQuery(id));
  const catalog = useQuery(publicCatalogQuery);

  if (plan.isLoading) {
    return (
      <main className="page-shell">
        <p>Memuat lesson plan…</p>
      </main>
    );
  }
  if (!plan.data?.ok) {
    return (
      <main className="page-shell">
        <StatePanel
          variant="error"
          title="Lesson plan tidak dapat dimuat"
          description="Coba segarkan halaman ini."
        />
      </main>
    );
  }

  return (
    <main className="page-shell print-shell">
      <div className="print-actions print-screen-only">
        <button
          type="button"
          className="button button-primary"
          onClick={() => window.print()}
          data-testid="print-trigger"
        >
          Cetak sekarang
        </button>
      </div>
      <PrintLayout
        plan={plan.data.data}
        itemTitles={new Map([
          ...(catalog.data?.worksheets ?? []).map((worksheet): [string, string] => [
            `worksheet:${worksheet.worksheetId}`,
            worksheet.judul,
          ]),
          ...(catalog.data?.games ?? []).map((game): [string, string] => [
            `game:${game.gameId}`,
            game.title,
          ]),
        ])}
      />
    </main>
  );
}
